import { useContext } from 'react';

import type {
  Langs,
  LangCopy,
  TranslationCopy,
  TranslationContent
} from '@CS/components/TranslationProvider/types';

import { Context } from '@CS/components/TranslationProvider';

const showTranslationError = (copy: LangCopy, lang: string) => {
  console.error(
    `😱 Translation for "${lang}" language was not provided in`,
    copy,
    '\n',
    'Please, add a proper translation in the content object or provide a fallback language to reuse an existing one.'
  );

  return '';
};

const translateCopy = (
  copy: TranslationCopy,
  lang: Langs,
  fallback?: Langs
) => {
  if (typeof copy === 'string') return copy;
  if (Object.hasOwn(copy, lang)) return copy[lang];
  return fallback && copy[fallback] || showTranslationError(copy, lang);
};

const translateContent = (
  content: TranslationContent,
  lang: Langs,
  fallback?: Langs,
  translations: Record<keyof typeof content, string> = {}
) => {
  for (const copy in content)
    translations[copy] = translateCopy(content[copy], lang, fallback);

  return translations;
};

type Translations = Record<string, string>;

export default function useTranslation<
  T extends TranslationContent | TranslationCopy
>(x: T): T extends TranslationCopy ? string : Translations;

export default function useTranslation(
  content: TranslationContent | TranslationCopy,
  fallback?: Langs
): Translations | string {
  const lang = useContext(Context) as Langs;

  return typeof content === 'string' || Object.hasOwn(content, lang)
    ? translateCopy(content as TranslationCopy, lang, fallback)
    : translateContent(content, lang, fallback);
}
